import * as React from "react"
import { useQuery, useMutation } from "@apollo/client"
import { ErrorBoundary } from "react-error-boundary"
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from "recoil"
import { CoordinateSystemUnit, ProjectCreatedWithStatus, Point, PolicyType } from "../../types"
import {
  GET_CONFIG,
  HAS_ACCESS,
  SYNC_PROJECT,
  ELIGIBLE_ACCOUNTS,
  GET_CRSUNIT,
  IS_ALLOWED_TO_ENABLE_AND_REMOVE_EXTENSION,
  GET_REGIONS_FROM_SETTINGS_API,
  GET_GLOBAL_SETTINGS,
  GET_USER_RIGHTS,
  HAS_PROJECT_OWNER_RIGHTS,
  IS_CUSTOMER_HANDLED_BY_TCPS
} from "../../api/QuadriApi"
import ConnectApi, { ConnectData } from "../../api/ConnectApi"
import {
  addSnackSelector,
  extensionApiState,
  projectIdState,
  projectOriginState,
  projectRegionState,
  projectUsersState,
  quadriProjectIdState,
  signedInUserIdState,
  signedInUserState,
  userProjectRoleState
} from "../StateSelectors"
import { PendingSpinner } from "../Pending"
import { OopsFallback } from "../Oops/OopsFallback"
import QuadriProjectSettings from "./QuadriProjectSettings"
import QuadriHeader from "./QuadriHeader"
import QuadriFooter from "../Config/QuadriFooter"
import { setMenu } from "../../util/setMenu"
import { useTranslation } from "react-i18next"
import { exceptionFromApolloError } from "../../util/apolloError"
import MissingLicense from "../MissingLicense"
import { SnackProps } from "../Config/SnackBars"
import { track } from "../../util/gainsight"
import GetEnvironment from "../../environments"
import { checkIfAllowedToEnableOrRemoveExtension } from "../Config/utils"
import ConfigMainStart from "./ConfigMainStart"
import ConfigMain from "../Config/ConfigMain"
import { areasMatch, crsNamesAreEqual } from "../../util/projectSettings"
import { datesAreEqual } from "../../util/dateTools"
import { Suspense } from "react"
import { HAS_PROJECT_ACCESS } from "../../api/QuadriApi"
import RestrictedAccess from "./RestrictedAccess"
import PermissionsPanel from "../Config/PermissionsPanel"

const BACKUP_RELOAD_WARNING = 10000
const BACKUP_RELOAD_TIMEOUT = 15000

// Used to determine the config page state. Exported so that the progress banner can use it
export type ConfigStatus = "Creating" | "Active" | "Failed" | "Updating" | "Default" | "Deleting"

// The exported wrapper component that handles error boundary and input
// to the loading component
const ObjectModelConfigMain = () => {
  const projectId = useRecoilValue(projectIdState)
  const projectOrigin = useRecoilValue(projectOriginState)
  const signedInUser = useRecoilValue(signedInUserState)
  const signedInUserId = useRecoilValue(signedInUserIdState)

  return (
    <>
      <ErrorBoundary FallbackComponent={OopsFallback}>
        <ConfigLoad
          projectId={projectId}
          projectOrigin={projectOrigin}
          signedInUser={signedInUser}
          signedInUserId={signedInUserId}
        />
      </ErrorBoundary>
    </>
  )
}

interface ConfigLoadProps {
  projectId?: string
  projectOrigin?: string
  accessToken?: string
  signedInUser?: string
  signedInUserId?: string
}

// TODO: Shorten this somehow
// The responsibility of this is to load everything necessary to render the
// Config page. It must therefore also hold initial project state as well
// as data from Connect
const ConfigLoad = (props: ConfigLoadProps) => {
  const { projectId, projectOrigin, signedInUser, signedInUserId } = props
  const { t } = useTranslation(["common", "config"])

  if (!projectId) throw new Error("Cannot load config page: Missing project id")
  if (!projectOrigin) throw new Error("Cannot load config page: Missing project origin")

  const [, setQuadriProjectId] = useRecoilState(quadriProjectIdState)
  const extensionApi = useRecoilValue(extensionApiState)
  const [, setProjectRegionState] = useRecoilState(projectRegionState)
  const [projectStatus, setProjectStatus] = React.useState(undefined as ConfigStatus | undefined)

  // Connect Data
  const [connectApi, setConnectApi] = React.useState<ConnectApi | undefined>(undefined)
  const [connectData, setConnectData] = React.useState<ConnectData>({})
  const [fetchedConnect, setFetchedConnect] = React.useState(false)
  const [ownershipAccount, setOwnershipAccount] = React.useState("")
  const [CrsName, SetCrsName] = React.useState<string>()

  const accessQuery = useQuery(HAS_ACCESS)
  const rightsQuery = useQuery(GET_USER_RIGHTS)

  const accountsQuery = useQuery(ELIGIBLE_ACCOUNTS)

  const crsUnitQuery = useQuery(GET_CRSUNIT, {
    variables: { crsName: CrsName ?? "" },
    skip: CrsName === undefined
  })

  const projectQuery = useQuery(GET_CONFIG, {
    variables: { id: projectId },
    onCompleted: (data) => {
      const updatedStatus = data?.tryGetConnectQuadriProject?.project?.projectStatus
      if (typeof updatedStatus !== "undefined") {
        setProjectStatus(updatedStatus)
        console.log("Setting project status: ", updatedStatus)
      }

      const quadriProjectId = data?.tryGetConnectQuadriProject?.project?.quadriProjectId
      if (quadriProjectId) {
        setQuadriProjectId(quadriProjectId)
        console.log("Set Quadri Project Id: ", quadriProjectId)
      }
    }
  })

  // Load role from async Recoil selector
  const projectUserRoleLoadable = useRecoilValueLoadable(userProjectRoleState)

  const [hasProjectAccess, setHasProjectAccess] = React.useState(false)
  const [projectUserRole, setProjectUserRole] = React.useState<string | undefined>(undefined)

  const { state: roleState, contents: roleContents } = projectUserRoleLoadable

  // Handle error from Connect API (role fetch failure)
  // Let the ErrorBoundary handle role fetch failures

  if (roleState === "hasError") {
    throw roleContents
  }

  // Only allow query if:
  // 1. The project exists OR forceProjectAccessQuery is enabled
  // 2. The role is resolved and is a valid non-empty string
  const roleIsReady =
    roleState === "hasValue" &&
    typeof roleContents === "string" &&
    roleContents.trim() !== "" &&
    roleContents === "ADMIN"
  const skipProjectAccessQuery = !roleIsReady || !projectQuery.data?.tryGetConnectQuadriProject?.found

  // This Query is just used here for syncying purpose. (No syncying needed if a user has ROLE == USER as a USER does not has access to Object Model Config Page).
  // Run HAS_PROJECT_ACCESS only if role === "ADMIN"
  const projectAccessQuery = useQuery(HAS_PROJECT_ACCESS, {
    skip: skipProjectAccessQuery,
    variables: {
      connectProjectId: projectId,
      connectProjectUserRole: roleIsReady ? roleContents : ""
    }
  })

  React.useEffect(() => {
    if (projectAccessQuery.data?.hasProjectAccess) {
      setHasProjectAccess(projectAccessQuery.data.hasProjectAccess.projectAccess)
      setProjectUserRole(projectAccessQuery.data.hasProjectAccess.role)
    }
  }, [projectAccessQuery.data])

  const getGlobalSettingsQuery = useQuery(GET_GLOBAL_SETTINGS)

  const getRegionsQuery = useQuery(GET_REGIONS_FROM_SETTINGS_API)

  const { data } = projectQuery

  localStorage.setItem("extensionRemoved", "false")

  React.useEffect(() => {
    const runEffectAsync = async () => {
      const api = new ConnectApi(projectOrigin)
      setConnectApi(api)
      const connectData = await api.getConnectProjectData(projectId)
      console.log("Connect data: ", connectData)
      setConnectData(connectData)
      SetCrsName(connectData.crsName)
      setOwnershipAccount(connectData.connectProject?.license?.accounts[0]?.name)
      setProjectRegionState(connectData.region)
      setFetchedConnect(true)
    }
    runEffectAsync()

    const handleOpen = () => {
      console.log("Reloading connect data from event")
      runEffectAsync()
    }

    window.addEventListener("objectModelConfig:open", handleOpen)
    return () => window.removeEventListener("objectModelConfig:open", handleOpen)
  }, [projectOrigin, projectId, setProjectRegionState])

  React.useEffect(() => {
    console.log("Showing extension menu items")
    console.log("GET CONFIG query data: ", data)
    setMenu(t, projectStatus, extensionApi)
  }, [extensionApi, projectStatus, data, t])

  if (projectAccessQuery.error) throw exceptionFromApolloError(projectAccessQuery.error)
  if (accessQuery.error) throw exceptionFromApolloError(accessQuery.error)
  if (accountsQuery.error) throw exceptionFromApolloError(accountsQuery.error)
  if (projectQuery.error) throw exceptionFromApolloError(projectQuery.error)
  if (crsUnitQuery.error) throw exceptionFromApolloError(crsUnitQuery.error)
  if (getGlobalSettingsQuery.error) throw exceptionFromApolloError(getGlobalSettingsQuery.error)
  if (getRegionsQuery.error) throw exceptionFromApolloError(getRegionsQuery.error)
  if (rightsQuery.error) throw exceptionFromApolloError(rightsQuery.error)

  // From here, the queries are either loading, or they have data
  if (
    !fetchedConnect ||
    projectUserRoleLoadable.state === "loading" ||
    projectAccessQuery.loading ||
    accessQuery.loading ||
    accountsQuery.loading ||
    projectQuery.loading ||
    crsUnitQuery.loading ||
    getGlobalSettingsQuery.loading ||
    getRegionsQuery.loading ||
    rightsQuery.loading
  ) {
    return <PendingSpinner />
  } else {
    const needsLicense = !accessQuery.data.hasAccess?.access
    const licenseType = accessQuery.data.hasAccess?.licenseType
    const rights = rightsQuery.data.getUserRights?.rights

    const noAccounts = !accountsQuery.data.eligibleAccountsWithLicenses

    if (needsLicense) {
      return (
        <RestrictedAccess
          showIcon={true}
          showBackgroundImage={false}
          title={t("restrictedAccess.headingForUserLicenseFail", { ns: "config" })}
          description={t("restrictedAccess.NoQuadriLicense", { ns: "config" })}
          buttonLabel={t("restrictedAccess.upgrade", { ns: "config" })}
          onButtonClick={() =>
            window.open("https://www.trimble.com/en/products/trimble-connect/#pricing-cards-1", "_blank")
          }
        />
      )
    }
    if (noAccounts) {
      return (
        <MissingLicense
          heading={t("customerNotEligible.heading", { ns: "config" })}
          body1={t("customerNotEligible.body1", { ns: "config" })}
          body2={t("customerNotEligible.body2", { ns: "config" })}
          moreInfo={t("forMoreInfo.moreInfo", { ns: "common" })}
          quadriPlans={t("forMoreInfo.quadriPlans", { ns: "common" })}
          info={ownershipAccount}
          andCheck={t("forMoreInfo.andCheck", { ns: "common" })}
          subscriptions={t("forMoreInfo.subscriptions", { ns: "common" })}
        ></MissingLicense>
      )
    }

    let quadriConfigurationUiUrl
    if (getGlobalSettingsQuery.data) {
      quadriConfigurationUiUrl = getGlobalSettingsQuery.data.getGlobalSettings.quadriConfigurationFrontEndUrl
    }

    // do we have a project?
    const { found, project } = projectQuery.data.tryGetConnectQuadriProject

    //do we have crsUnit?
    let crsUnit
    if (crsUnitQuery.data) {
      crsUnit = crsUnitQuery.data.getCrsUnit.unitName
    }

    const innerProps = {
      connectProjectId: projectId,
      featureCatalogId: project?.featureCatalogId,
      extensionOwnershipId: project?.customerAccountId,
      extensionOwnershipName: project?.customerAccountName,
      extensionLicenseId: project?.productId,
      coordinateSystemUnit: crsUnit || project?.coordinateSystemUnit,
      coordinateSystemName: project?.coordinateSystemName,
      projectFound: found,
      connectData: connectData,
      noAccounts,
      projectStatus,
      projectStatusMessage: project?.projectStatusMessage,
      projectUpdateMessage: project?.projectUpdateMessage,
      projectName: project?.projectName,
      projectDescription: project?.projectDescription,
      projectStartDate: project?.projectStartDate,
      projectEndDate: project?.projectEndDate,
      projectOwnershipId: project?.customerAccountId,
      createdAt: project?.createdAt,
      updatedAt: project?.updatedAt,
      permissionsUpdatedAt: project?.permissionsUpdatedAt,
      projectCreatedWithStatus: project?.projectCreatedWithStatus,
      tcpsArea: project?.projectArea,
      crsUnit,
      quadriConfigurationUiUrl,
      setProjectStatus,
      quadriProjectId: project?.quadriProjectId,
      projectUserRole: projectUserRole || (roleIsReady ? (roleContents as string) : ""),
      signedInUserId: signedInUserId || "",
      signedInUser: signedInUser || "",
      isTcpsLicensed: project?.isTcpsLicensed,
      rights: rights,
      licenseType: licenseType,
      connectProjectOwnershipIdfromTCPS: project?.connectProjectOwnershipId,
      connectProjectOwnershipNamefromTCPS: project?.connectProjectOwnershipName
    }

    return (
      <Suspense fallback={<PendingSpinner />}>
        <ConfigMainInner {...innerProps} />
      </Suspense>
    )
  }
}

interface ConfigMainInnerProps {
  connectProjectId: string
  connectData: ConnectData
  featureCatalogId?: string
  extensionOwnershipId: string
  extensionOwnershipName: string
  extensionLicenseId: string
  coordinateSystemUnit?: CoordinateSystemUnit
  coordinateSystemName?: string
  projectFound?: boolean
  projectStatus?: ConfigStatus
  projectStatusMessage?: string
  projectUpdateMessage?: string
  projectName?: string
  projectDescription?: string
  projectStartDate?: string
  projectEndDate?: string
  projectOwnershipId?: string
  createdAt?: string
  updatedAt?: string
  permissionsUpdatedAt?: string
  projectCreatedWithStatus?: ProjectCreatedWithStatus
  tcpsArea?: Point[]
  crsUnit: string | undefined
  quadriConfigurationUiUrl: string
  setProjectStatus: (status: ConfigStatus) => void
  quadriProjectId: string
  projectUserRole: string | undefined
  signedInUserId: string
  signedInUser: string
  isTcpsLicensed: boolean
  rights: string[]
  licenseType: string
  connectProjectOwnershipIdfromTCPS?: string
  connectProjectOwnershipNamefromTCPS?: string
}

// This is the inner component responsible for page interaction and rendering
// It is assumed that all required data is loaded when this is rendered
const ConfigMainInner = (props: ConfigMainInnerProps) => {
  const { region } = props.connectData
  const {
    projectStatus,
    projectStatusMessage,
    createdAt,
    updatedAt,
    permissionsUpdatedAt,
    connectProjectId,
    signedInUserId,
    signedInUser
  } = props
  const { setProjectStatus, quadriProjectId } = props
  const { projectUpdateMessage, projectCreatedWithStatus } = props
  const projectUsers = useRecoilValue(projectUsersState)
  const [, addSnack] = useRecoilState(addSnackSelector)
  const { t } = useTranslation("config")

  // Feature catalog state is kept here and not in child QuadriProjectSettings component
  // because it is used in the creation request that is tied to the button.
  const [extensionOwnership, setExtensionOwnership] = React.useState<string | undefined>(props.extensionOwnershipId)
  const [extensionOwnershipName, setExtensionOwnershipName] = React.useState<string | undefined>(
    props.extensionOwnershipName
  )
  const [accountAdmins, setAccountAdmins] = React.useState([])
  const [featureCatalogId, setFeatureCatalogId] = React.useState<string | undefined>(props.featureCatalogId)
  const [featureCatalogStandard, setFeatureCatalogStandard] = React.useState<string | undefined>(undefined)
  const [coordinateSystemUnit, setCoordinateSystemUnit] = React.useState<CoordinateSystemUnit | undefined>(
    props.coordinateSystemUnit
  )
  const [showMessage, setShowMessage] = React.useState({
    show: false,
    for: "",
    accountName: extensionOwnershipName
  })
  const [isAllowedToEnableAndRemoveExtensionbyPolicy, setIsAllowedToEnableAndRemoveExtensionbyPolicy] =
    React.useState<boolean>(true)

  const [allowedByRegionPolicy, setAllowedByRegionPolicy] = React.useState<boolean>(true)
  const [allowedRegions, setAllowedRegions] = React.useState<string>("")
  const [syncStarted, setSyncStarted] = React.useState(false)
  const [settingsChanged, setSettingsChanged] = React.useState(false)
  const [hasProjectOwnerAdminRights, setHasProjectOwnerAdminRights] = React.useState(false)
  const [hasProjectOwnerClassicAdminRights, setHasProjectOwnerClassicAdminRights] = React.useState(false)
  const hasRight = (rightName: any) => props.rights?.includes(rightName)
  const isProjectAdmin = projectUsers.some((user) => user.id === signedInUserId && user.role === "ADMIN")
  const [showPermissionsPanel, setShowPermissionsPanel] = React.useState(true)

  const isHandledByTcpsQuery = useQuery(IS_CUSTOMER_HANDLED_BY_TCPS, {
    variables: { customerAccountId: extensionOwnership },
    skip: !extensionOwnership
  })

  const isHandledByTcps = isHandledByTcpsQuery.error
    ? false
    : isHandledByTcpsQuery.data?.isCustomerHandledByTcps.isHandledByTcps

  const isHandledByTcpsLoading = !props.extensionOwnershipId ? false : isHandledByTcpsQuery.loading
  // Save the id of the existing feature catalog if there is one. This is used
  // to fetch a list of compatible feature catalogs if the user wants to upgrade
  const existingFcId = props.featureCatalogId || null
  const shouldSkipQuery =
    !GetEnvironment().enforceExtensionActivationAndRemovalPolicy ||
    extensionOwnership === undefined ||
    props.licenseType?.toLowerCase() === "quadribusiness"

  //Add query to get extension activation and removal policy
  const { loading, error, data } = useQuery(IS_ALLOWED_TO_ENABLE_AND_REMOVE_EXTENSION, {
    variables: { customerAccountId: extensionOwnership ?? "" },
    skip: shouldSkipQuery || isHandledByTcpsQuery.loading || isHandledByTcps === false
  })

  const {
    data: projectOwnerRightsData,
    loading: projectOwnerRightsDataLoading,
    error: accountRightsDataError
  } = useQuery(HAS_PROJECT_OWNER_RIGHTS, {
    variables: { connectProjectId: connectProjectId, usecases: ["Admin", "ClassicAdmin"], region: region },
    skip: !(isProjectAdmin && hasRight("Write"))
  })

  React.useEffect(() => {
    if (!projectOwnerRightsDataLoading && !accountRightsDataError && projectOwnerRightsData) {
      const rights = projectOwnerRightsData?.hasProjectOwnerRights?.rights || []

      const adminRight = rights.find((r: any) => r.usecase === "Admin").right
      const classicAdminRight = rights.find((r: any) => r.usecase === "ClassicAdmin").right

      setHasProjectOwnerAdminRights(adminRight)
      setHasProjectOwnerClassicAdminRights(classicAdminRight)
    }
    if (accountRightsDataError) {
      throw exceptionFromApolloError(accountRightsDataError)
    }
  }, [projectOwnerRightsData, accountRightsDataError, projectOwnerRightsDataLoading])

  // Mutation used for syncing after the project has been created
  const [syncProject, syncProjectResponse] = useMutation(SYNC_PROJECT, {
    onCompleted: (data: any) => {
      startBackupTimeoutReload(t, addSnack)
      setSyncStarted(false)
    }
  })

  React.useEffect(() => {
    if (projectStatus === "Failed" && projectStatusMessage) {
      addSnack({
        type: "error",
        message: projectStatusMessage,
        duration: 3000
      })
    }
  }, [projectStatus, projectStatusMessage, addSnack])

  const closeMessageModal = () => {
    setShowMessage({ show: false, for: "", accountName: "" })
  }

  if (loading || projectOwnerRightsDataLoading) {
    return <PendingSpinner />
  }

  // Callback function to Create/Sync button
  const submitQuadriProject = (e?: any) => {
    if (syncStarted) {
      return
    }

    setSyncStarted(true)
    e?.preventDefault()

    //On Submit first check here if project is to be activated and if allowed by policy? if yes execute the activation else show message
    if (!props.projectFound && !isAllowedToEnableAndRemoveExtensionbyPolicy) {
      setShowMessage({
        show: true,
        for: PolicyType.extensionActivationAndRemovalPolicy,
        accountName: extensionOwnershipName
      })
      return
    } else if (!allowedByRegionPolicy) {
      setShowMessage({ show: true, for: PolicyType.regionPolicy, accountName: extensionOwnershipName })
      return
    }

    if (
      connectProjectId !== undefined &&
      featureCatalogId !== undefined &&
      coordinateSystemUnit !== undefined &&
      region !== undefined &&
      props.connectData.connectProject?.license?.accounts[0]?.accountId !== undefined
    ) {
      const project = {
        connectProjectId: connectProjectId,
        featureCatalogId,
        coordinateSystemUnit,
        connectProjectRegion: region,
        customerAccountId: props.connectData.connectProject?.license?.accounts[0]?.accountId, // send project ownership from Connect
        isTcpsLicensed: props.isTcpsLicensed
      }

      if (projectStatus === "Active") {
        // Sync project request is the same as create project, but without customerAccountId
        // since it cannot be changed after creation
        track("project_update", { connectProjectId: connectProjectId })
        const syncProjectRequest = { ...project } as any
        syncProjectRequest.extensionOwnership = extensionOwnership
        if (extensionOwnership === props.connectProjectOwnershipIdfromTCPS) {
          delete syncProjectRequest.extensionOwnership
        }
        setProjectStatus("Updating")
        console.log("Updating")
        syncProject({ variables: { project: syncProjectRequest } }).catch((error) => {
          // Error handling - trigger snack on failure
          addSnack({
            type: "error",
            message: error.message ?? "Failed to update the project",
            duration: 3000
          })
          setProjectStatus("Active") // Reset status to active
          setSyncStarted(false) // Stop sync
        })
      }
    } else {
      console.error("Missing update parameters")
    }
  }

  // Get status of the config page, this is a combination of project status and query statuses
  const configStatus = projectStatus || "Default"

  // Boolean for if an asynchronous operation is in progress
  const changeInProgress = configStatus === "Creating" || configStatus === "Updating" || configStatus === "Deleting"

  // Disable update if a sync is in progress or if no changes have been made
  const isEnableButtonDisabled = changeInProgress || !settingsChanged

  // Disable editing the feature catalogs
  const disableEdit = changeInProgress || !isProjectAdmin
  const disableEditCoordinateSystemUnit = disableEdit || !!props.crsUnit

  // Check if the project config is in sync between Connect and TCPS

  const stringsMatch = (a: string | undefined, b: string | undefined) => {
    // undefined is considered equal to empty string
    return (a || "").trim() === (b || "").trim()
  }

  const areaMismatch = !areasMatch(props.connectData.boundaryCoordinates, props.tcpsArea || [])
  const crsNameMismatch = !crsNamesAreEqual(props.connectData.crsName, props.coordinateSystemName)
  const projectNameMismatch = !stringsMatch(props.projectName, props.connectData.connectProject?.name)
  const projectDescriptionMismatch = !stringsMatch(
    props.projectDescription,
    props.connectData.connectProject?.description
  )
  const projectStartDateMismatch = !datesAreEqual(props.projectStartDate, props.connectData.connectProject?.startDate)
  const projectEndDateMismatch = !datesAreEqual(props.projectEndDate, props.connectData.connectProject?.endDate)
  const projectOwnershipMismatch =
    props.connectProjectOwnershipIdfromTCPS !== props.connectData.connectProject?.license?.accounts[0]?.accountId

  const needSync =
    areaMismatch ||
    crsNameMismatch ||
    projectNameMismatch ||
    projectDescriptionMismatch ||
    projectStartDateMismatch ||
    projectEndDateMismatch ||
    projectOwnershipMismatch

  if (needSync && !changeInProgress && props.projectUserRole === "ADMIN" && projectStatus === "Active") {
    console.warn("ObjectModelConfigMain: Project config is not in sync between Connect and TCPS")
    submitQuadriProject()
  }

  const objectModelIsEnabled = !!props.projectFound

  return (
    <>
      {objectModelIsEnabled ? (
        !isProjectAdmin ? (
          <RestrictedAccess
            showIcon={false}
            showBackgroundImage={false}
            description={t("restrictedAccess.NotAdminUpdate", { ns: "config" })}
          />
        ) : props.isTcpsLicensed ? (
          <ConfigMain rights={props.rights} disableAutoSync={true} />
        ) : (
          <div style={{ paddingLeft: "16px", paddingRight: "16px" }}>
            <div className="row align-items-stretch" style={{ minHeight: "100vh" }}>
              {/* Left side (main content) */}
              <div className={showPermissionsPanel ? "col-9" : "col-12"}>
                <QuadriHeader
                  configStatus={configStatus}
                  isEnableButtonHidden={!(isProjectAdmin && hasRight("Write"))}
                  isEnableButtonDisabled={isEnableButtonDisabled}
                  statusMessage={projectStatusMessage}
                  updateErrorMessage={projectUpdateMessage}
                  createProjectResponse={undefined}
                  submitQuadriProject={submitQuadriProject}
                  createdAt={createdAt}
                  updatedAt={updatedAt}
                  permissionsUpdatedAt={permissionsUpdatedAt}
                  projectCreatedWithStatus={projectCreatedWithStatus}
                  showMessage={showMessage}
                  closeMessageModal={closeMessageModal}
                  allowedRegions={allowedRegions}
                  accountAdmins={accountAdmins}
                  openPanel={() => setShowPermissionsPanel(true)}
                  showPermissionsPanel={showPermissionsPanel}
                />
                <QuadriProjectSettings
                  existingFcId={existingFcId}
                  fcStandard={featureCatalogStandard}
                  setFcStandard={setFeatureCatalogStandard}
                  featureCatalogId={featureCatalogId}
                  setFeatureCatalogId={setFeatureCatalogId}
                  coordinateSystemUnits={coordinateSystemUnit}
                  setCoordinateSystemUnits={setCoordinateSystemUnit}
                  disableEdit={disableEdit}
                  disableEditCoordinateSystemUnit={disableEditCoordinateSystemUnit}
                  configStatus={configStatus}
                  projectStatus={projectStatus}
                  region={region}
                  quadriConfigurationUiUrl={props.quadriConfigurationUiUrl}
                  onChange={() => setSettingsChanged(true)}
                  isHandledByTcps={isHandledByTcps}
                  isHandledByTcpsLoading={isHandledByTcpsLoading}
                />
                <QuadriFooter
                  connectProjectId={connectProjectId}
                  projectStatus={projectStatus}
                  changeInProgress={changeInProgress}
                  isAllowedToEnableAndRemoveExtensionbyPolicy={isAllowedToEnableAndRemoveExtensionbyPolicy}
                  extensionOwnershipName={extensionOwnershipName}
                  accountAdmins={accountAdmins}
                  isTcpsLicensed={props.isTcpsLicensed}
                  isRemoveButtonHidden={!(isProjectAdmin && hasRight("Write"))}
                />
              </div>
              {showPermissionsPanel && (
                <PermissionsPanel
                  isEnableButtonHidden={!(isProjectAdmin && hasRight("Write"))}
                  onClose={() => setShowPermissionsPanel(false)}
                  quadriProjectId={quadriProjectId}
                  connectProjectId={connectProjectId}
                  disabled={configStatus !== "Active"}
                />
              )}
            </div>
          </div>
        )
      ) : (
        // NEW logic when objectModelIsEnabled is false
        (() => {
          if (isProjectAdmin && hasRight("Write")) {
            if (GetEnvironment().enableNewCommonUi) {
              return <ConfigMainStart quadriEnabled={!!props.projectFound} submitQuadriProject={submitQuadriProject} />
            } else {
              if (hasProjectOwnerClassicAdminRights) {
                return (
                  <ConfigMainStart
                    quadriEnabled={!!props.projectFound}
                    submitQuadriProject={submitQuadriProject}
                    openClassicUI={true}
                  />
                )
              } else {
                if (hasProjectOwnerAdminRights) {
                  return (
                    <ConfigMainStart quadriEnabled={!!props.projectFound} submitQuadriProject={submitQuadriProject} />
                  )
                } else {
                  return (
                    <RestrictedAccess
                      showIcon={true}
                      showBackgroundImage={false}
                      title={t("restrictedAccess.headingForAccountLicenseFail", { ns: "config" })}
                      description={t("restrictedAccess.ProjectOwnerLicense", { ns: "config" })}
                      buttonLabel={t("restrictedAccess.learnMore", { ns: "config" })}
                      onButtonClick={() => window.open("https://construction.trimble.com/en/products/quadri", "_blank")}
                    />
                  )
                }
              }
            }
          } else {
            if (isProjectAdmin) {
              if (props.licenseType?.toLowerCase() === "quadribusiness") {
                return (
                  <RestrictedAccess
                    showIcon={true}
                    showBackgroundImage={false}
                    title={t("restrictedAccess.headingForUserLicenseFail", { ns: "config" })}
                    description={t("restrictedAccess.NoQBPLicense", { ns: "config" })}
                    buttonLabel={t("restrictedAccess.upgrade", { ns: "config" })}
                    onButtonClick={() => window.open("https://construction.trimble.com/en/contact-us", "_blank")}
                  />
                )
              } else {
                return (
                  <RestrictedAccess
                    showIcon={true}
                    showBackgroundImage={false}
                    title={t("restrictedAccess.headingForUserLicenseFail", { ns: "config" })}
                    description={t("restrictedAccess.NoQuadriLicense", { ns: "config" })}
                    buttonLabel={t("restrictedAccess.upgrade", { ns: "config" })}
                    onButtonClick={() =>
                      window.open("https://www.trimble.com/en/products/trimble-connect/#pricing-cards-1", "_blank")
                    }
                  />
                )
              }
            } else {
              return (
                <RestrictedAccess
                  showIcon={false}
                  showBackgroundImage={false}
                  description={t("restrictedAccess.NotAdminActivate", { ns: "config" })}
                />
              )
            }
          }
        })()
      )}
    </>
  )
}

export const startBackupTimeoutReload = (t: any, addSnack: (props: SnackProps) => void) => {
  const snackTimer = setTimeout(() => {
    console.log(`Warning snackbar since no kafka-notification was received after ${BACKUP_RELOAD_WARNING} ms`)
    addSnack(getBackupReloadWarnSnackProps(t, BACKUP_RELOAD_WARNING))
  }, BACKUP_RELOAD_WARNING)
  clearTimeout(snackTimer)
  const reloadtimer = setTimeout(() => {
    console.log(`Reloading page since no kafka-notification was received after ${BACKUP_RELOAD_TIMEOUT} ms`)
    window.location.reload()
  }, BACKUP_RELOAD_TIMEOUT)
  clearTimeout(reloadtimer)
}

const getBackupReloadWarnSnackProps = (t: any, timeMs: number): SnackProps => ({
  type: "warning",
  message: t("backupReloadWarning.warning")
})

export default ObjectModelConfigMain
